import {
  Autocomplete,
  Box,
  Chip,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { CloseIcon } from 'assets/icons';
import { REGEX } from 'constant/regex';
import { FC, useEffect, useRef, useState } from 'react';

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface AppAutoCompleteProps {
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  limitTags?: number;
  options?: Option[];
  valueType?: 'string' | 'array';
  value?: string[] | string;
  defaultValue?: string[] | string;
  maxValue?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: any;
}

const getOptionLabel = (option: string | Option) => {
  return typeof option === 'string' ? option : option.label;
};

const TagInput: FC<AppAutoCompleteProps> = ({
  id,
  required,
  label,
  limitTags,
  options = [],
  placeholder,
  valueType = 'string',
  value,
  defaultValue,
  maxValue,
  onChange,
  error,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [values, setValues] = useState<string[]>([]);
  const isComposing = useRef(false);

  const handleCompositionStart = () => {
    isComposing.current = true;
  };

  const handleCompositionEnd = () => {
    isComposing.current = false;
  };

  useEffect(() => {
    if (defaultValue) {
      setValues(
        typeof defaultValue === 'string'
          ? defaultValue.split(',')
          : defaultValue
      );
    }
  }, []);

  useEffect(() => {
    if (value) {
      setValues(typeof value === 'string' ? value.split(',') : value);
    }
  }, [value]);

  const addTag = (newValue: string) => {
    if (maxValue != null && values.length >= maxValue) {
      setInputValue('');
      return;
    }

    if (newValue) {
      const oldValueIdx = values.findIndex((tag) => tag === newValue);
      if (oldValueIdx > -1) {
        values.splice(oldValueIdx, 1);
      }

      const newValues = values.concat(newValue);
      setValues(newValues);
      onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
    }

    setInputValue('');
  };

  const handleChange = (
    _event: React.SyntheticEvent | null,
    newValue: (string | Option)[]
  ) => {
    const newValues = newValue
      .map((value) => {
        if (typeof value === 'object') {
          if (!newValue.includes(value.label)) return getOptionLabel(value);
          return '';
        } else {
          return getOptionLabel(value);
        }
      })
      .filter((item) => item !== '');
    setValues(newValues);
    onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value.trim().replace(/[\u3000]/g, ''));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      ['Enter', ' ', '　', ','].includes(event.key) ||
      event.code === 'Space' ||
      event.code === ''
    ) {
      const target = event.target as HTMLInputElement;
      if (!isComposing.current) {
        addTag(target.value);
        event.preventDefault();
      }
    } else if (REGEX.SPECIAL.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleDelete = (option: string | Option) => {
    const deleteValue = getOptionLabel(option);
    const newValues = values.filter((ele) => ele !== deleteValue);
    setValues(newValues);
    onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
  };

  const handleBlur = () => {
    addTag(inputValue);
  };

  return (
    <Grid
      container
      direction={'column'}
      sx={{ gap: { xs: '4px', sm: '10px' } }}
    >
      {label && (
        <Grid item xs={4}>
          <FormLabel
            htmlFor={id}
            required={required}
            sx={{
              color: '#000000de',
              '& .MuiFormLabel-asterisk': { color: '#ff0000' },
            }}
          >
            {label}
          </FormLabel>
        </Grid>
      )}
      <Grid
        item
        xs={8}
        sx={{
          width: '100%',
          height: 'max-content',
          '& .MuiInputBase-input': {
            py: '4.63px !important',
          },
        }}
      >
        <Autocomplete
          id={id}
          multiple
          freeSolo
          size='small'
          limitTags={limitTags}
          getOptionLabel={getOptionLabel}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={getOptionLabel(option)}
                  {...tagProps}
                  color='primary'
                  deleteIcon={<CloseIcon fontSize='small' />}
                  onDelete={() => handleDelete(option)}
                />
              );
            })
          }
          inputValue={inputValue}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={!value ? placeholder : ''}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
            />
          )}
          options={options}
          renderOption={(props, option) => (
            <Box
              component='li'
              {...props}
              key={typeof option === 'string' ? option : option.value}
            >
              <Typography
                component='span'
                sx={{
                  fontSize: '20px !important',
                }}
              >
                {typeof option === 'string' ? option : option.label}
              </Typography>
            </Box>
          )}
          value={values}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

export default TagInput;
